/**
 * SlackPostInstallConfirmation — shown on the integrations page after a
 * successful Slack OAuth install (when ?justInstalled=slack is in the URL).
 *
 * Fetches /api/slack/install-summary to display:
 *   - How many YAGNI users were auto-linked via email match
 *   - How many still need to link their identity
 *   - CTA links to set up morning Edition delivery and Desk channels
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface InstallSummary {
  workspaceName: string;
  slackTeamName: string | null;
  linkedCount: number;
  yagniUsersCount: number;
}

export function SlackPostInstallConfirmation() {
  const [summary, setSummary] = useState<InstallSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const res = await fetch("/api/slack/install-summary", {
        credentials: "include",
      });
      if (res.ok) {
        setSummary((await res.json()) as InstallSummary);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div
        className="text-sm py-4"
        style={{ color: "var(--text-secondary)" }}
        data-testid="slack-install-loading"
      >
        Loading install summary…
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const displayName = summary.slackTeamName ?? summary.workspaceName;
  const unlinked = Math.max(0, summary.yagniUsersCount - summary.linkedCount);

  return (
    <div
      // Print material: the install already happened and the auto-link ran,
      // so this card is a committed record, not a staged decision.
      className="rounded-[var(--radius-card)] border p-5 mb-6"
      style={{
        background: "var(--print-bg)",
        borderColor: "var(--print-border)",
        boxShadow: "var(--shadow-print)",
      }}
      data-testid="slack-post-install-confirmation"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
          style={{ background: "var(--accent)" }}
        >
          S
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold" style={{ color: "var(--text-primary)" }}>
            Slack connected
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Connected to {displayName}. @yagni is ready to receive mentions and DMs.
          </p>

          <ul
            className="mt-3 text-sm space-y-1"
            style={{ color: "var(--text-secondary)" }}
          >
            <li data-testid="slack-linked-count">
              Auto-linked {summary.linkedCount} of {summary.yagniUsersCount} YAGNI{" "}
              {summary.yagniUsersCount === 1 ? "user" : "users"} via email match.
            </li>
            {unlinked > 0 && (
              <li data-testid="slack-unlinked-count">
                {unlinked} {unlinked === 1 ? "user" : "users"} not yet linked. They
                will be prompted when they first message @yagni.
              </li>
            )}
          </ul>

          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/settings"
              className="inline-flex items-center px-3 py-1.5 rounded text-sm font-medium text-white"
              style={{ background: "var(--accent)" }}
            >
              Daily Brief settings
            </Link>
            <Link
              to="/desks"
              className="inline-flex items-center px-3 py-1.5 rounded text-sm border"
              style={{
                color: "var(--text-primary)",
                borderColor: "var(--border)",
              }}
            >
              Set up Team channels
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
